/**
 * Twin identity helpers — how a Quick Study persona is named, blurbed and joined to
 * its answer.
 *
 * All that survives of the QS-35 / QS-37 "meet the panel" right-pane tab: the panel
 * component, its drill-down and the converse hand-off have no caller left. The pure
 * helpers below are shared by every surface that attributes a quote to a twin
 * (`InsightCard`, `QuickStudyOpenEndCard`, `MonadicOpenEndCard`, `PlotRenderer`,
 * `study-doc/enrichStudyDocQuote`, `StudyDocMagazineBand/buildRowQuotes`), so the file
 * keeps its path even though nothing in it renders any more.
 *
 * Pure / deterministic / no React.
 */
import { displayAttrValue } from './audience-segments'

// The element shape returned by populationsApi.getPersonas(...).personas — the
// same objects QuickStudyPage already fetches. Kept loose (extra keys allowed)
// so the panel never fights the API type.
export interface QuickStudyPersona {
  personaId: string
  personaName: string
  personaPrompt?: string
  /** User-facing bio from display_bio / featured-twin long_quote (API). */
  displayBio?: string | null
  /** Featured-twin long quote when present (API). */
  personaSummary?: string | null
  attributes?: Record<string, any>
  normalizedAttributes?: Record<string, any>
  imageUrl?: string
  personaNarrative?: string | null
  resolvedIdentity?: {
    display_name?: string
    age_range?: string
    country?: string
    gender?: string
    ethnicity?: string
  } | null
  [k: string]: any
}


// One twin's answer to the latest insight. `persona_id` DOES travel on the
// Quick Study /run result and is the primary join key (see
// `findResponseForPersona` below). It stays optional on the type because a few
// legacy saved sessions and non-standard callers omit it; attribute overlap is
// a defensive fallback for that miss, not the normal path.
export interface QuickStudyTwinResponse {
  persona_id?: string
  survey_data: Record<string, any>
  attributes: Record<string, any>
  // Some runs carry a free-text rationale alongside the structured answer; shown
  // verbatim in the drill-down when present.
  reasoning?: string | null
  // QS LP QA #23 — echoes Persona.segment_labels for this twin (the draw IS the
  // membership); [] / absent for a baseline twin AND for every non-segment-aware run.
  // Same field the quote-card chip reads (QuickStudyOpenEndCard's OpenEndResponse).
  segment_labels?: string[]
  [k: string]: any
}

const Q_NAME = 'qs_main'

// ── pure helpers (deterministic; exported for tests) ────────────────────────

export function twinDisplayName(p: QuickStudyPersona): string {
  return (p.resolvedIdentity?.display_name || p.personaName || 'Twin').trim()
}

export function twinInitials(p: QuickStudyPersona): string {
  const name = twinDisplayName(p)
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('') || 'T'
}



// QS LP QA #7 — B2B card identity: a title-ish attribute key (job title / role /
// position / seniority) and a company-ish key (company / employer / industry /
// company size / department). Mirrors the backend firmographic lens
// (`_FIRMOGRAPHIC_KEY_RE`, analysis.py) split into its two identity-line halves; kept
// independent (TS, not a verbatim port) to avoid cross-language drift, same discipline
// as `isConsumerDemographicAttr` in audience-segments.ts.
const B2B_TITLE_KEY_RE = /\b(job\s*title|title|role|position|seniority|job\s*level|job\s*function)\b/i
const B2B_COMPANY_KEY_RE =
  /\b(company|employer|firm|organization|industry|sector|vertical|company\s*size|team\s*size|org(anization)?\s*size|department|dept)\b/i

function isVerboseIdentityValue(value: string): boolean {
  const normalized = value.trim()
  if (!normalized) return false
  const wordCount = normalized.split(/\s+/).filter(Boolean).length
  return normalized.length > 60 || wordCount > 10
}

// A bare integer is a Likert answer, never an identity descriptor. Real audience attributes
// are keyed by the FULL question sentence, so a scale question that merely contains "company"
// or "role" ("How important is X to your company's success?") matches the firmographic regexes
// above, and its answer is far too short to trip `isVerboseIdentityValue` — which is how a bare
// "7" ended up rendering as a twin's identity under their name on a quote card.
// Deliberately narrower than "has no letters": a numeric RANGE is a real firmographic
// ("500-1000" employees, "$5B - $9.9B" revenue) and must keep rendering. Only a lone integer,
// with no separator, unit or qualifier, carries no identity at all.
function isScalePointValue(value: string): boolean {
  return /^\d+$/.test(value.trim())
}

function firstMatchingAttrValue(attrs: Record<string, any>, re: RegExp): string {
  for (const [key, raw] of Object.entries(attrs)) {
    if (!key || key.startsWith('_')) continue
    if (raw == null || typeof raw === 'object') continue
    if (!re.test(humanizeKey(key))) continue
    const value = String(raw).trim()
    if (!value) continue
    const rendered = displayAttrValue(value)
    // Skip long free-text profile blobs (for example role-authority paragraphs) so
    // quote attribution keeps a short identity line.
    if (isVerboseIdentityValue(rendered)) continue
    if (isScalePointValue(rendered)) continue
    return rendered
  }
  return ''
}

// Bug 12 — the demographic identity groups for the quote/tagline subtitle, in display order.
// Each group yields ONE value (first present alias). EXACT `age` is deliberately absent so it
// can never take a slot from `country` (the old QUOTE_DEMO_KEYS capped at 3 and let a 69 push
// the country off the line). Consumer line = age_range · gender · country.
const QUOTE_DEMO_GROUPS: Array<Array<string>> = [['age_range'], ['gender', 'sex'], ['country', 'region']]

function demographicBlurb(attrs: Record<string, any>): string {
  const seen = new Set<string>()
  const parts: string[] = []
  for (const group of QUOTE_DEMO_GROUPS) {
    for (const key of group) {
      const raw = attrs[key]
      if (raw == null || typeof raw === 'object') continue
      const value = displayAttrValue(String(raw).trim())
      const lower = value.toLowerCase()
      if (!value || seen.has(lower)) continue
      seen.add(lower)
      parts.push(value)
      break // one value per group
    }
  }
  return parts.join(' · ')
}

/**
 * Bug 12 — the ONE quote-card subtitle builder, shared by BOTH quote paths (a matched
 * persona and an unmatched response's own attributes) so every quote card reads the same
 * way. Rule: a B2B firm/role line ("Title · Company descriptor") when a title-ish and/or
 * company-ish attribute is present; else the consumer demographic line
 * (age_range · gender · country). Purely data-driven — never keyed on an explicit
 * audience-type flag (General Algorithm Requirement). Degrades gracefully: only ONE B2B part
 * present → that part alone; nothing groundable → "". Pure — unit-tested directly.
 */
export function quoteIdentityBlurb(attributes: Record<string, any> | undefined): string {
  const attrs = attributes ?? {}
  const title = firstMatchingAttrValue(attrs, B2B_TITLE_KEY_RE)
  const company = firstMatchingAttrValue(attrs, B2B_COMPANY_KEY_RE)
  const b2b = [title, company].filter(Boolean).join(' · ')
  return b2b || demographicBlurb(attrs)
}

/**
 * QS LP QA #7 — the quote-card identity line for a MATCHED persona. Thin wrapper over the
 * shared `quoteIdentityBlurb`: B2B firmographic keys live on `attributes`; the canonical
 * demographics on `resolvedIdentity` (which wins for age/gender/country when both carry a key).
 * NEITHER present → "".
 */
export function twinIdentityTagline(p: QuickStudyPersona): string {
  return quoteIdentityBlurb({ ...(p.attributes ?? {}), ...(p.resolvedIdentity ?? {}) })
}

const DEMO_KEYS = ['age', 'gender', 'sex', 'country', 'region', 'income', 'education', 'occupation', 'job', 'role']
const SURVEY_KEY = /^qs_|^q\d|\?$/i

function humanizeKey(name: string): string {
  const s = name.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim()
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : name
}

/**
 * The handful of clean, human-readable attributes worth showing on a persona
 * card: skip internal `_`-prefixed keys, the survey-answer key, long free-text
 * blobs and object values; demographic keys float to the top.
 */
function twinKeyAttributes(
  p: QuickStudyPersona,
  max = 6,
): Array<{ key: string; label: string; value: string }> {
  const merged: Record<string, any> = { ...(p.attributes ?? {}), ...(p.resolvedIdentity ?? {}) }
  const out: Array<{ key: string; label: string; value: string }> = []
  for (const [key, raw] of Object.entries(merged)) {
    if (!key || key.startsWith('_')) continue
    if (key === Q_NAME || SURVEY_KEY.test(key)) continue
    if (key === 'display_name') continue
    if (raw == null || typeof raw === 'object') continue
    const value = String(raw).trim()
    if (!value || value.length > 60) continue
    out.push({ key, label: humanizeKey(key), value })
  }
  out.sort((a, b) => demoRank(a.key) - demoRank(b.key))
  return out.slice(0, max)
}

function demoRank(key: string): number {
  const i = DEMO_KEYS.findIndex((d) => key.toLowerCase().includes(d))
  return i === -1 ? DEMO_KEYS.length : i
}


/**
 * Resolve a persona's response from the latest insight. persona_id travels on
 * the /run result and is the primary join; attribute overlap on the clean
 * demographic attributes is a defensive fallback for the rare response that
 * arrives without one (legacy saved sessions, non-standard callers).
 * Returns undefined when no confident match exists — we never guess.
 */
export function findResponseForPersona(
  persona: QuickStudyPersona,
  responses: QuickStudyTwinResponse[] | undefined,
): QuickStudyTwinResponse | undefined {
  if (!responses?.length) return undefined
  const byId = responses.find((r) => r.persona_id && r.persona_id === persona.personaId)
  if (byId) return byId

  const target = twinKeyAttributes(persona)
  if (!target.length) return undefined
  // Score each response by how many of the persona's key attrs it matches; require
  // a strong, unambiguous match (every key present + agreeing) to avoid mislabeling.
  let best: QuickStudyTwinResponse | undefined
  let bestScore = 0
  let tie = false
  for (const r of responses) {
    const attrs = { ...(r.attributes ?? {}) }
    let score = 0
    let mismatch = false
    for (const { key, value } of target) {
      const rv = attrs[key]
      if (rv == null) continue
      if (String(rv).trim() === value) score += 1
      else mismatch = true
    }
    if (mismatch) continue
    if (score > bestScore) {
      bestScore = score
      best = r
      tie = false
    } else if (score === bestScore && score > 0) {
      tie = true
    }
  }
  // Need a clear, non-trivial, non-tied match.
  return !tie && bestScore >= Math.min(2, target.length) ? best : undefined
}

/**
 * A twin's AUTHORED subsegment labels (Persona.segment_labels — the draw IS the
 * membership), read off its response to the latest insight — the same data source
 * the quote-card chip uses (QuickStudyOpenEndCard). `[]` before any insight has run,
 * for a non-segment-aware run, or for a baseline twin with no authored subsegment.
 * Deduped and trimmed; never derived from demographics (General Algorithm Requirement).
 */
export function twinSubsegmentLabels(
  persona: QuickStudyPersona,
  responses: QuickStudyTwinResponse[] | undefined,
): string[] {
  const labels = findResponseForPersona(persona, responses)?.segment_labels
  if (!Array.isArray(labels)) return []
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of labels) {
    const label = String(raw ?? '').trim()
    if (label && !seen.has(label)) {
      seen.add(label)
      out.push(label)
    }
  }
  return out
}
